import React, { useState } from "react";
import { services, whatsappLink } from "../data/site";
import { ArrowUpRight } from "./Icons";
import Reveal from "./Reveal";
import SectionLabel from "./SectionLabel";
import useMediaQuery from "../hooks/useMediaQuery";
import {
  btnBase,
  btnGhost,
  btnGhostSweep,
  btnIcon,
  btnLabel,
  btnSweep,
  cx,
  display,
  inner,
} from "../lib/ui";

const badge =
  "absolute left-0 bottom-0 z-10 bg-paper px-4 py-2.5 text-[11px] font-semibold tracking-[0.2em] text-ink";

export default function Services() {
  const [active, setActive] = useState(0);
  const isDesktop = useMediaQuery("(min-width: 980px)");
  const current = services[active];

  return (
    <section id="services" className="relative scroll-mt-nav overflow-hidden bg-ink py-section text-paper">
      <div className={cx(inner, "px-gutter")}>
        <header className="grid gap-[clamp(22px,4vw,60px)] border-b border-white/15 pb-[clamp(38px,5vw,70px)] lg:grid-cols-[200px_1fr] lg:items-start lg:gap-x-[clamp(24px,4vw,60px)]">
          <SectionLabel className="!text-ash-300">What we do</SectionLabel>
          <h2 className={cx(display, "text-[clamp(38px,6.6vw,92px)] lg:col-start-2")}>
            <Reveal as="span" mask>
              Four verticals,
            </Reveal>
            <Reveal as="span" mask delay={90} className="italic pl-[clamp(0px,6vw,110px)]">
              one standard.
            </Reveal>
          </h2>
        </header>

        {isDesktop ? (
          /* ---------- desktop: hover-driven list, sticky picture ---------- */
          <div className="grid grid-cols-[1fr_minmax(360px,40%)] items-start gap-[clamp(30px,4vw,72px)] pt-[clamp(24px,3vw,46px)]">
            <ul className="grid">
              {services.map((service, i) => {
                const on = i === active;
                return (
                  <Reveal
                    as="li"
                    key={service.id}
                    delay={i * 80}
                    className="border-b border-white/15"
                  >
                    <button
                      type="button"
                      onMouseEnter={() => setActive(i)}
                      onFocus={() => setActive(i)}
                      onClick={() => setActive(i)}
                      aria-pressed={on}
                      className={cx(
                        "group grid w-full grid-cols-[54px_1fr_34px] items-center gap-3 py-[clamp(20px,2.4vw,34px)] text-left transition-[padding] duration-[550ms] ease-out",
                        on && "pl-4"
                      )}
                    >
                      <span
                        className={cx(
                          "text-[11px] tracking-[0.2em] transition-colors duration-[350ms]",
                          on ? "text-paper" : "text-ash-400"
                        )}
                      >
                        {service.index}
                      </span>
                      <span
                        className={cx(
                          display,
                          "text-[clamp(26px,3.4vw,52px)] transition-colors duration-[450ms] ease-out group-hover:text-paper",
                          on ? "text-paper" : "text-ash-400"
                        )}
                      >
                        {service.title}
                      </span>
                      <span
                        className={cx(
                          "grid place-items-center transition-[opacity,transform,color] duration-[450ms] ease-out",
                          on
                            ? "translate-x-0 translate-y-0 text-paper opacity-100"
                            : "-translate-x-2 translate-y-2 text-ash-400 opacity-0"
                        )}
                      >
                        <ArrowUpRight width="18" height="18" />
                      </span>
                    </button>
                  </Reveal>
                );
              })}
            </ul>

            <Reveal
              className="sticky top-[calc(theme(spacing.nav)+28px)] grid gap-[22px]"
              delay={140}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-ink-800">
                {services.map((service, i) => (
                  <img
                    key={service.id}
                    src={service.image}
                    alt={service.title}
                    loading="lazy"
                    className={cx(
                      "absolute inset-0 h-full w-full object-cover grayscale contrast-[1.1] brightness-90 transition-[opacity,transform] duration-[850ms] ease-out",
                      i === active ? "scale-100 opacity-100" : "scale-110 opacity-0"
                    )}
                  />
                ))}
                <span className={badge}>{current.index}</span>
              </div>

              <div key={current.id} className="grid animate-fade-up gap-[18px]">
                <p className="text-[15px] leading-[1.7] text-ash-200">{current.summary}</p>

                <ul className="grid">
                  {current.points.map((point, i) => (
                    <li
                      key={point}
                      className="flex animate-slide-in-x items-center gap-3 border-t border-white/15 py-[11px] text-[13px] tracking-[0.03em] text-ash-300"
                      style={{ animationDelay: `${i * 70}ms` }}
                    >
                      <i className="block h-[5px] w-[5px] shrink-0 rotate-45 bg-paper" />
                      {point}
                    </li>
                  ))}
                </ul>

                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={cx(btnBase, btnGhost, "justify-self-start")}
                >
                  <span className={cx(btnSweep, btnGhostSweep)} />
                  <span className={btnLabel}>Enquire — {current.title}</span>
                  <ArrowUpRight width="15" height="15" className={btnIcon} />
                </a>
              </div>
            </Reveal>
          </div>
        ) : (
          /* ---------- mobile: full cards, nothing hidden behind hover ---------- */
          <ul className="grid gap-11 pt-10">
            {services.map((service, i) => (
              <Reveal as="li" key={service.id} delay={i * 70} className="grid gap-3.5">
                <div className="relative aspect-[3/2] overflow-hidden bg-ink-800">
                  <img
                    src={service.image}
                    alt={service.title}
                    loading="lazy"
                    className="h-full w-full object-cover grayscale contrast-[1.1] brightness-[0.92]"
                  />
                  <span className={badge}>{service.index}</span>
                </div>
                <h3 className={cx(display, "text-[clamp(26px,7vw,38px)] text-paper")}>
                  {service.title}
                </h3>
                <p className="text-[15px] leading-[1.7] text-ash-200">{service.summary}</p>
                <ul className="grid">
                  {service.points.map((point) => (
                    <li
                      key={point}
                      className="flex items-center gap-3 border-t border-white/15 py-[11px] text-[13px] text-ash-300"
                    >
                      <i className="block h-[5px] w-[5px] shrink-0 rotate-45 bg-paper" />
                      {point}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
